import { ChevronDown, ChevronUp, Plus, X } from 'lucide-react'
import AddressAutocomplete from '@/components/address/AddressAutocomplete'

// 中途停靠點（附加乘客）：可新增多點上下車，各點可填姓名／電話，並可調整順序
export interface Stop { address: string; name: string; phone: string }

const smallInput = 'w-full border border-gray-300 rounded-lg px-2.5 py-2 text-sm outline-none focus:ring-2 focus:ring-[#969737]/50 bg-white'
const iconBtn = 'p-1.5 rounded-md bg-gray-100 text-gray-500 hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer transition-colors'

export default function StopsEditor({ stops, onChange, extraCharge, onExtraChargeChange }: {
  stops: Stop[]; onChange: (s: Stop[]) => void; extraCharge?: number; onExtraChargeChange?: (n: number) => void
}) {
  const patch = (i: number, p: Partial<Stop>) => onChange(stops.map((s, idx) => (idx === i ? { ...s, ...p } : s)))
  const move = (i: number, to: number) => {
    const next = [...stops]
    ;[next[i], next[to]] = [next[to], next[i]]
    onChange(next)
  }

  return (
    <div className="space-y-2">
      {stops.map((stop, i) => (
        <div key={i} className="flex gap-2 items-start bg-[#f5f7fa] rounded-xl p-2.5">
          <div className="flex-1 space-y-2 min-w-0">
            <p className="text-xs font-medium text-gray-600">停靠點 {i + 1}（上／下車）</p>
            <div className="grid grid-cols-2 gap-1.5">
              <input className={smallInput} placeholder="姓名（選填）" value={stop.name} onChange={e => patch(i, { name: e.target.value })} />
              <input className={smallInput} placeholder="電話（選填）" inputMode="tel" value={stop.phone} onChange={e => patch(i, { phone: e.target.value })} />
            </div>
            <AddressAutocomplete value={stop.address} onChange={v => patch(i, { address: v })} placeholder="停靠點地址" />
          </div>
          <div className="flex flex-col gap-1 shrink-0">
            <button type="button" className={iconBtn} disabled={i === 0} onClick={() => move(i, i - 1)}><ChevronUp size={14} /></button>
            <button type="button" className={iconBtn} disabled={i === stops.length - 1} onClick={() => move(i, i + 1)}><ChevronDown size={14} /></button>
            <button type="button" className="p-1.5 rounded-md bg-red-50 text-red-400 hover:bg-red-100 cursor-pointer transition-colors" onClick={() => onChange(stops.filter((_, idx) => idx !== i))}><X size={14} /></button>
          </div>
        </div>
      ))}
      {onExtraChargeChange && stops.length > 0 && (
        <label className="flex items-center justify-between gap-3 text-sm text-gray-700">
          <span>加價</span>
          <span className="flex items-center gap-1">
            $<input type="number" min={0} inputMode="numeric" className={`${smallInput} !w-28 text-right`} placeholder="0"
              value={extraCharge || ''} onChange={e => onExtraChargeChange(Math.max(parseInt(e.target.value) || 0, 0))} />
          </span>
        </label>
      )}
      <button
        type="button"
        onClick={() => onChange([...stops, { address: '', name: '', phone: '' }])}
        className="w-full flex items-center justify-center gap-1.5 py-2.5 rounded-xl border border-dashed border-[#969737] text-[#7f802e] text-sm hover:bg-[#f1f1dc] cursor-pointer transition-colors"
      >
        <Plus size={14} />新增停靠點（多點上下車）
      </button>
    </div>
  )
}

export const toStopPayload = (stops: Stop[]) =>
  stops.filter(s => s.address.trim()).map((s, i) => ({
    address: s.address, order: i + 1, name: s.name.trim() || undefined, phone: s.phone.trim() || undefined,
  }))
